
import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { LoginComponent } from '../../pages/auth/login/login';
import { AuthenticationService } from '../../core/services/authentication.service';
import { AuthorizationService } from '../../core/authorization/authorization.service';
import { Permissions } from '../../core/authorization/permissions';
import { NotificationsTrayComponent } from '../notifications/notifications-tray';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, DatePipe, LoginComponent, NotificationsTrayComponent],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class HeaderComponent implements OnInit {
  private readonly router = inject(Router);
  readonly auth = inject(AuthenticationService);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;
  currentDate = new Date();
  showLoginModal = false;
  showUserMenu = false;
  /** Controls the collapsed mobile navigation drawer. */
  mobileMenuOpen = false;

  get isLoggedIn(): boolean {
    return this.auth.isLoggedIn();
  }

  get currentUser(): any {
    return this.auth.getCurrentUser();
  }

  get userName(): string {
    const user = this.currentUser;
    if (!user) return '';
    return user.firstName || user.email || user.userId || 'User';
  }

  get userRole(): string {
    const role = this.auth.getUserRole();
    return role
      .trim()
      .toLowerCase()
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join('');
  }

  get isAdmin(): boolean {
    return this.authorization.hasRole('admin');
  }

  get isStudent(): boolean {
    return this.authorization.hasRole('student') || this.authorization.hasRole('alumni');
  }

  /** Home link — points to dashboard when logged in, otherwise /home */
  get homeLink(): string {
    if (this.isLoggedIn && this.isAdmin) return '/admin';
    if (this.isLoggedIn && this.isStudent) return '/student';
    return '/home';
  }

  ngOnInit(): void {
    // React to auth state changes if needed
  }

  isActive(path: string): boolean {
    return this.router.url.startsWith(path);
  }

  isAdminDashboardActive(): boolean {
    return this.router.url === '/admin';
  }

  isStudentSectionActive(section: string): boolean {
    return this.router.url.startsWith('/student')
      && this.router.parseUrl(this.router.url).queryParamMap.get('section') === section;
  }

  isAdminSectionActive(section: string): boolean {
    return this.router.url.startsWith('/admin')
      && this.router.parseUrl(this.router.url).queryParamMap.get('section') === section;
  }

  isStudentDashboardActive(): boolean {
    if (!this.router.url.startsWith('/student')) return false;
    const section = this.router.parseUrl(this.router.url).queryParamMap.get('section');
    return !section || section === 'dashboard';
  }

  /** The shared header breadcrumb is only rendered on dashboard/profile pages. */
  get showHeaderBreadcrumb(): boolean {
    if (!this.isLoggedIn) return false;
    const url = this.router.url;
    return url.startsWith('/student') || url.startsWith('/admin') || url.startsWith('/profile')
      || url.startsWith('/flight-requests');
  }

  isProfilePage(): boolean {
    return this.router.url.startsWith('/profile');
  }

  /** The standalone flight booking route (the same page is also a student section). */
  isFlightRequestsPage(): boolean {
    return this.router.url.startsWith('/flight-requests');
  }

  /** Opens/closes the collapsed mobile navigation drawer. */
  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  /** Closes the mobile navigation drawer (e.g. after a nav link is chosen). */
  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  openLogin(): void {
    this.showLoginModal = true;
  }

  closeLogin(): void {
    this.showLoginModal = false;
  }

  logout(): void {
    this.mobileMenuOpen = false;
    this.auth.clearSession();
    this.router.navigate(['/home']);
  }

  goToDashboard(): void {
    this.mobileMenuOpen = false;
    if (this.isAdmin) {
      this.router.navigate(['/admin'], { state: { redirectUnauthenticatedToLogin: true } });
    } else if (this.isStudent) {
      this.router.navigate(['/student'], { state: { redirectUnauthenticatedToLogin: true } });
    } else {
      this.router.navigate(['/home']);
    }
  }
}
